// ContactsPanel — the people behind a company, for the CompanyDetail tab.
//
// The ONE question an operator opens this panel to answer is "who from this
// company ends up on a document, and who doesn't". So the list is grouped by
// that answer rather than sorted by it: a "Reaches documents" section and a
// "Reaches nothing" section, with the count in the header. Reading a print_on
// value out of a column and mapping it back to a consequence is work, and work
// is where mistakes live.
//
// Two things drive that grouping, and only one of them is obvious:
//
//   print_on  the operator's intent (none | receipt | invoice | both)
//   active    a hard gate — go-pim's ContactsForDocument selects
//             `WHERE ... AND active AND print_on IN ($docType,'both')`
//
// So a contact set to print but marked inactive reaches NOTHING, and nothing on
// a print_on column alone would ever say so. That combination is filed under
// "Reaches nothing" with a warn pill explaining why, because the alternative is
// an operator who ticked "both" believing a signatory is on their invoices.
//
// Writes are their own sub-resource (POST/PUT/DELETE on contacts), never a
// nested array on the company payload — go-pim's contacts.go explains why: the
// company PUT is a full-row replace, and a client that omitted `contacts` would
// delete every one of them.
import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Pill, EmptyState, type PillTone } from "@/components/UI";
import { ConfirmDialog, type ConfirmRequest } from "@/components/ConfirmDialog";
import { apiErrorMessage } from "@/utils/apiError";
import { useUI } from "@/store/ui";
import { useAuth } from "@/store/auth";
import {
  listContacts,
  createContact,
  updateContact,
  deleteContact,
  printsOn,
  CONTACT_ROLES,
  CONTACT_ROLE_LABELS,
  PRINT_ONS,
  PRINT_ON_LABELS,
  MAX_CONTACT_NAME_LEN,
  MAX_CONTACT_DESIGNATION_LEN,
  MAX_CONTACT_FREE_TEXT_LEN,
  type Contact,
  type ContactWritePayload,
  type ContactRole,
  type PrintOn,
} from "@/api/contacts";

/**
 * What this contact actually reaches, as opposed to what print_on says.
 *
 * Derived from the api module's `printsOn` rather than re-reading print_on, so
 * the grouping cannot drift from the server's selection — that predicate is the
 * one place the `AND active` gate is mirrored.
 *
 * The labels are written out here instead of reusing PRINT_ON_LABELS because
 * they answer a different question: PRINT_ON_LABELS names the SETTING for a
 * <select> ("Print on receipts"), this names the CURRENT STATE for a pill.
 */
function documentReach(c: Contact): {
  printing: boolean;
  tone: PillTone;
  label: string;
} {
  const onReceipt = printsOn(c, "receipt");
  const onInvoice = printsOn(c, "invoice");
  if (onReceipt && onInvoice)
    return { printing: true, tone: "pos", label: "On receipts + invoices" };
  if (onReceipt) return { printing: true, tone: "pos", label: "On receipts" };
  if (onInvoice) return { printing: true, tone: "pos", label: "On invoices" };
  // Reaches nothing — but for two very different reasons, and conflating them
  // is the trap this panel exists to make visible. Intent says print, the
  // active gate says otherwise, and the gate wins.
  if (c.print_on !== "none")
    return {
      printing: false,
      tone: "warn",
      label: "Inactive — set to print, but suppressed",
    };
  return { printing: false, tone: "", label: "Filing only" };
}

export function ContactsPanel({ companyId }: { companyId: string }) {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const authStatus = useAuth((s) => s.status);

  const queryKey = ["contacts", companyId];
  const {
    data: contacts = [],
    isLoading,
    isError,
  } = useQuery<Contact[]>({
    queryKey,
    queryFn: () => listContacts(companyId),
    enabled: authStatus === "authenticated" && companyId !== "",
    staleTime: 60_000,
  });

  // `undefined` = editor closed, `null` = creating, a Contact = editing it.
  const [editing, setEditing] = useState<Contact | null | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);

  // Failures ride the same channel as successes (variant "error" is announced
  // assertively by ToastStack), so an operator never has to look somewhere else
  // on the page to find out an action didn't happen.
  const fail = (err: unknown, fallback: string) =>
    addToast({
      title: fallback,
      body: apiErrorMessage(err, ""),
      variant: "error",
    });

  const groups = useMemo(() => {
    // go-pim already returns printing contacts first, then sort_order, then
    // name; filter is stable, so both groups keep that order.
    const printing = contacts.filter((c) => documentReach(c).printing);
    const silent = contacts.filter((c) => !documentReach(c).printing);
    // Called out separately in the header: these are the ones whose setting and
    // whose effect disagree.
    const suppressed = silent.filter((c) => c.print_on !== "none");
    return { printing, silent, suppressed };
  }, [contacts]);

  const saveMutation = useMutation({
    mutationFn: (payload: ContactWritePayload) =>
      editing ? updateContact(editing.id, payload) : createContact(companyId, payload),
    onSuccess: async (saved) => {
      addToast({
        title: editing ? "Contact saved" : "Contact added",
        body: saved?.name,
      });
      setEditing(undefined);
      setFormError(null);
      await qc.invalidateQueries({ queryKey });
    },
    onError: (err) =>
      setFormError(apiErrorMessage(err, "Could not save that contact. Try again.")),
  });

  // Inline print_on change straight from the row — the fastest way to answer
  // "put this person on invoices" without opening the editor.
  //
  // PUT /v1/contacts/:id is a FULL-ROW replace, so this sends every writable
  // field taken from the row it is changing. Sending only print_on would blank
  // the designation, phone and notes of whoever the operator was trying to help.
  const printOnMutation = useMutation({
    mutationFn: ({ contact, printOn }: { contact: Contact; printOn: PrintOn }) =>
      updateContact(contact.id, { ...toPayload(contact), print_on: printOn }),
    // Paint it immediately, or the <select> snaps back to its old value on the
    // next render and only corrects itself when the refetch lands — which looks
    // exactly like the server rejected the change.
    onMutate: async ({ contact, printOn }) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<Contact[]>(queryKey);
      if (previous) {
        qc.setQueryData<Contact[]>(
          queryKey,
          previous.map((c) =>
            c.id === contact.id ? { ...c, print_on: printOn } : c,
          ),
        );
      }
      return { previous };
    },
    onSuccess: (saved) =>
      addToast({
        title: "Contact updated",
        body: `${saved.name} — ${documentReach(saved).label.toLowerCase()}.`,
      }),
    // Roll back, or the row keeps advertising a reach the contact doesn't have.
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
      fail(err, "Could not change where that contact prints");
    },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteContact(id),
    onSuccess: async (_d, id) => {
      setConfirm(null);
      if (editing?.id === id) setEditing(undefined);
      addToast({
        title: "Contact deleted",
        body: "Documents already issued are unchanged.",
      });
      await qc.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      setConfirm(null);
      fail(err, "Could not delete that contact");
    },
  });

  function askDelete(c: Contact) {
    setConfirm({
      title: `Delete ${c.name}?`,
      // The fear that stops people tidying this list is that an old receipt
      // will lose a name, or start showing a blank where one was. It can't:
      // a printing contact is copied into the document's issuer snapshot at
      // issue time and read from there forever. Say so, or the list only ever
      // grows.
      body:
        "Documents already issued are NOT affected — each one froze its own copy of the contacts printed on it when it was issued, and nothing here is read again when an old receipt or invoice is viewed." +
        (documentReach(c).printing
          ? ` ${c.name} will stop appearing on documents issued from now on.`
          : " This contact wasn't printing on anything, so no document changes at all."),
      confirmLabel: "Delete contact",
      destructive: true,
      onConfirm: () => deleteMutation.mutate(c.id),
    });
  }

  const reaching = groups.printing.length;

  return (
    <div className="stack-16">
      <ConfirmDialog
        request={confirm}
        busy={deleteMutation.isPending}
        onClose={() => setConfirm(null)}
      />

      <div className="card">
        <div className="card-head">
          <h3>Contacts</h3>
          <div className="spacer" />
          <button
            className="btn accent sm"
            type="button"
            disabled={editing === null}
            onClick={() => {
              setFormError(null);
              setEditing(null);
            }}
          >
            <I.Plus size={12} /> Add contact
          </button>
        </div>

        <div className="card-body stack-12">
          <div className="dim" style={{ fontSize: 12, lineHeight: 1.55 }}>
            {isLoading
              ? "Loading…"
              : contacts.length === 0
                ? "Nobody is recorded for this company yet."
                : `${contacts.length} contact${contacts.length === 1 ? "" : "s"} — ${
                    reaching === 0
                      ? "none of them reach a document"
                      : `${reaching} reach${reaching === 1 ? "es" : ""} a document`
                  }.`}
          </div>

          {groups.suppressed.length > 0 && (
            // Setting and effect disagree here, and nothing else on the page
            // would ever say so.
            <div
              className="row gap-8"
              style={{ alignItems: "flex-start", color: "var(--warn)", fontSize: 12 }}
            >
              <I.AlertCircle size={13} />
              <div style={{ lineHeight: 1.55 }}>
                {groups.suppressed.length === 1
                  ? `${groups.suppressed[0].name} is set to print but is inactive, so nothing is printed. Mark this contact active to put them back on documents.`
                  : `${groups.suppressed.length} contacts are set to print but are inactive, so nothing is printed for them. Mark them active to put them back on documents.`}
              </div>
            </div>
          )}

          {editing !== undefined && (
            <ContactForm
              // Remount on target change so the fields re-seed from the row
              // being edited instead of carrying the previous one's values.
              key={editing?.id ?? "new"}
              initial={editing}
              busy={saveMutation.isPending}
              error={formError}
              onCancel={() => {
                setEditing(undefined);
                setFormError(null);
              }}
              onSubmit={(payload) => {
                setFormError(null);
                saveMutation.mutate(payload);
              }}
            />
          )}
        </div>

        {isLoading ? (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              Loading…
            </div>
          </div>
        ) : isError ? (
          <EmptyState
            icon={<I.AlertCircle size={16} />}
            title="Couldn't load contacts"
            body="The request to the go-pim admin API failed. The company's other details are unaffected."
          />
        ) : contacts.length === 0 ? (
          <EmptyState
            icon={<I.Users size={16} />}
            title="No contacts yet"
            body="Add the people behind this company — an authorised signatory to name on invoices, or simply who to call about one. A contact prints nowhere until you say it should."
            action={
              <button
                className="btn accent"
                type="button"
                disabled={editing === null}
                onClick={() => {
                  setFormError(null);
                  setEditing(null);
                }}
              >
                <I.Plus size={12} /> Add contact
              </button>
            }
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Person</th>
                <th>Filing role</th>
                <th>Prints on</th>
                <th>Reaches</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <GroupHeading
                show={groups.printing.length > 0}
                title={`Reaches documents · ${groups.printing.length}`}
                note="Copied onto every receipt or invoice issued from now on."
              />
              {groups.printing.map((c) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  editing={editing?.id === c.id}
                  busy={printOnMutation.isPending}
                  onPrintOn={(printOn) =>
                    printOnMutation.mutate({ contact: c, printOn })
                  }
                  onEdit={() => {
                    setFormError(null);
                    setEditing(c);
                  }}
                  onDelete={() => askDelete(c)}
                />
              ))}
              <GroupHeading
                show={groups.silent.length > 0}
                title={`Reaches nothing · ${groups.silent.length}`}
                note="Kept as a record here. No document shows these people."
              />
              {groups.silent.map((c) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  editing={editing?.id === c.id}
                  busy={printOnMutation.isPending}
                  onPrintOn={(printOn) =>
                    printOnMutation.mutate({ contact: c, printOn })
                  }
                  onEdit={() => {
                    setFormError(null);
                    setEditing(c);
                  }}
                  onDelete={() => askDelete(c)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Section divider inside the table — the grouping IS the answer, so it is
 *  labelled with the consequence rather than the field name. */
function GroupHeading({
  show,
  title,
  note,
}: {
  show: boolean;
  title: string;
  note: string;
}) {
  if (!show) return null;
  return (
    <tr>
      <td colSpan={5} style={{ padding: "14px 14px 6px" }}>
        <div
          style={{
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 600,
          }}
        >
          {title}
        </div>
        <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
          {note}
        </div>
      </td>
    </tr>
  );
}

function ContactRow({
  contact,
  editing,
  busy,
  onPrintOn,
  onEdit,
  onDelete,
}: {
  contact: Contact;
  editing: boolean;
  busy: boolean;
  onPrintOn: (p: PrintOn) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const reach = documentReach(contact);
  return (
    <tr className={editing ? "selected" : undefined}>
      <td>
        <div className="cell-name">{contact.name}</div>
        <div className="cell-id dim">
          {/* Designation is the printed line, so it is what the row leads with
              under the name. Blank is not an error — the document falls back to
              the filing role — but the operator should see which they'll get. */}
          {contact.designation !== ""
            ? contact.designation
            : `No designation — prints as “${roleAsPrinted(contact.role)}”`}
          {contact.email !== "" ? ` · ${contact.email}` : ""}
          {contact.phone !== "" ? ` · ${contact.phone}` : ""}
        </div>
      </td>
      <td className="dim">{CONTACT_ROLE_LABELS[contact.role]}</td>
      <td>
        <select
          className="input"
          style={{ maxWidth: 170 }}
          value={contact.print_on}
          disabled={busy}
          aria-label={`Documents ${contact.name} prints on`}
          onChange={(e) => onPrintOn(e.target.value as PrintOn)}
        >
          {PRINT_ONS.map((p) => (
            <option key={p} value={p}>
              {PRINT_ON_LABELS[p]}
            </option>
          ))}
        </select>
      </td>
      <td>
        <Pill tone={reach.tone}>{reach.label}</Pill>
      </td>
      <td className="right">
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn ghost sm" type="button" onClick={onEdit}>
            <I.Edit size={11} /> Edit
          </button>
          <button className="btn ghost sm" type="button" onClick={onDelete}>
            <I.Trash size={11} /> Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

/** How go-pim renders a role when designation is blank: underscores become
 *  spaces and the raw token goes on the document. Shown to the operator so the
 *  fallback is a choice rather than a surprise. */
function roleAsPrinted(role: ContactRole): string {
  return role.replace(/_/g, " ");
}

/** Every writable field of an existing contact, for the full-row PUT. */
function toPayload(c: Contact): ContactWritePayload {
  return {
    name: c.name,
    role: c.role,
    designation: c.designation,
    email: c.email,
    phone: c.phone,
    notes: c.notes,
    print_on: c.print_on,
    sort_order: c.sort_order,
    active: c.active,
  };
}

/**
 * The inline create/edit form. Rendered in the panel body rather than a modal
 * so the list it changes stays on screen while it is being changed.
 *
 * State is seeded once from `initial`; the parent remounts this component via
 * `key` when the target changes, which is why there is no re-seed effect here.
 */
function ContactForm({
  initial,
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  initial: Contact | null; // null = create
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (payload: ContactWritePayload) => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [role, setRole] = useState<ContactRole>(initial?.role ?? "other");
  const [designation, setDesignation] = useState(initial?.designation ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  // Defaults to "none" on create, matching the column default: adding a person
  // must never silently change what customers receive.
  const [printOn, setPrintOn] = useState<PrintOn>(initial?.print_on ?? "none");
  const [sortOrder, setSortOrder] = useState(String(initial?.sort_order ?? 0));
  const [active, setActive] = useState(initial?.active ?? true);
  const [nameError, setNameError] = useState<string | null>(null);

  const willPrint = printOn !== "none" && active;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (name.trim() === "") {
      setNameError("Name can't be empty — go-pim requires it.");
      return;
    }
    setNameError(null);
    const order = parseInt(sortOrder, 10);
    onSubmit({
      name: name.trim(),
      role,
      designation: designation.trim(),
      email: email.trim(),
      phone: phone.trim(),
      notes: notes.trim(),
      print_on: printOn,
      sort_order: Number.isNaN(order) ? 0 : order,
      active,
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="stack-12"
      style={{
        border: "1px solid var(--hair)",
        borderRadius: 3,
        padding: 14,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600 }}>
        {initial ? `Editing ${initial.name}` : "New contact"}
      </div>

      <div className="field-row">
        <div className="field">
          <label className="label" htmlFor="contact-name">
            Name
          </label>
          <input
            id="contact-name"
            className="input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Anita Raghunathan"
            maxLength={MAX_CONTACT_NAME_LEN}
            aria-invalid={nameError != null}
            autoFocus
          />
          {nameError && <div className="field-error">{nameError}</div>}
        </div>
        <div className="field">
          <label className="label" htmlFor="contact-designation">
            Designation <span className="opt">what prints</span>
          </label>
          <input
            id="contact-designation"
            className="input"
            type="text"
            value={designation}
            onChange={(e) => setDesignation(e.target.value)}
            placeholder="e.g. Managing Director"
            maxLength={MAX_CONTACT_DESIGNATION_LEN}
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            This is the line that appears on the document, verbatim. Free text on
            purpose — real titles vary by entity type, so go-pim never checks it
            against a list. Leave it blank and the document falls back to the
            filing role beside it: “{roleAsPrinted(role)}”.
          </div>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label className="label" htmlFor="contact-role">
            Filing role <span className="opt">doesn't print</span>
          </label>
          <select
            id="contact-role"
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as ContactRole)}
          >
            {CONTACT_ROLES.map((r) => (
              <option key={r} value={r}>
                {CONTACT_ROLE_LABELS[r]}
              </option>
            ))}
          </select>
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            How this person is filed here, and the only part of a contact code
            can rely on — which is why it is a fixed list. It does not decide
            what prints: “Prints on” below does that, for every role alike.
          </div>
        </div>
        <div className="field">
          <label className="label" htmlFor="contact-print-on">
            Prints on
          </label>
          <select
            id="contact-print-on"
            className="input"
            value={printOn}
            onChange={(e) => setPrintOn(e.target.value as PrintOn)}
          >
            {PRINT_ONS.map((p) => (
              <option key={p} value={p}>
                {PRINT_ON_LABELS[p]}
              </option>
            ))}
          </select>
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            {printOn === "none"
              ? "This person appears on no document at all — a record kept here only."
              : active
                ? `Name, designation, email and phone are copied onto each ${
                    printOn === "both"
                      ? "receipt and invoice"
                      : printOn === "receipt"
                        ? "receipt"
                        : "invoice"
                  } issued from now on. Documents already issued keep what they froze.`
                : "Inactive contacts print nowhere, whatever this says — tick Active below to make this take effect."}
          </div>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label className="label" htmlFor="contact-email">
            Email <span className="opt">prints when this contact prints</span>
          </label>
          <input
            id="contact-email"
            className="input"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={MAX_CONTACT_FREE_TEXT_LEN}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="contact-phone">
            Phone <span className="opt">prints when this contact prints</span>
          </label>
          <input
            id="contact-phone"
            className="input"
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={MAX_CONTACT_FREE_TEXT_LEN}
          />
        </div>
      </div>

      <div className="field">
        <label className="label" htmlFor="contact-notes">
          Notes <span className="opt">internal — never printed</span>
        </label>
        <textarea
          id="contact-notes"
          className="input"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={MAX_CONTACT_FREE_TEXT_LEN}
        />
      </div>

      <div className="row gap-16" style={{ alignItems: "flex-end" }}>
        <div className="field" style={{ width: 120 }}>
          <label className="label" htmlFor="contact-sort">
            Sort order
          </label>
          <input
            id="contact-sort"
            className="input"
            type="number"
            step={1}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Lower first, on the document as well as in this list.
          </div>
        </div>
        <label
          className="row gap-8"
          htmlFor="contact-active"
          style={{ cursor: "pointer", alignItems: "center", paddingBottom: 8 }}
        >
          <input
            id="contact-active"
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          <span style={{ fontSize: 13 }}>
            {active
              ? "Active"
              : "Inactive — kept as a record, and prints on nothing regardless of “Prints on”."}
          </span>
        </label>
      </div>

      {/* Restates the outcome once, in one line, right above the button that
          commits it — the reach of a contact is decided by two fields that sit
          apart from each other, and this is where they are added up. */}
      <div
        className="row gap-8"
        style={{ alignItems: "center", fontSize: 12, lineHeight: 1.5 }}
      >
        <Pill tone={willPrint ? "pos" : ""}>
          {willPrint
            ? printOn === "both"
              ? "Will print on receipts + invoices"
              : printOn === "receipt"
                ? "Will print on receipts"
                : "Will print on invoices"
            : "Will print on nothing"}
        </Pill>
        <span className="dim">
          {willPrint
            ? `as “${designation.trim() !== "" ? designation.trim() : roleAsPrinted(role)}”`
            : "stored here as a record only"}
        </span>
      </div>

      {error && <div style={{ color: "var(--neg)", fontSize: 12 }}>{error}</div>}

      <div className="row gap-8">
        <button
          className="btn ghost"
          type="button"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <div className="flex-1" />
        <button className="btn accent" type="submit" disabled={busy}>
          {busy ? "Saving…" : initial ? "Save changes" : "Add contact"}
        </button>
      </div>
    </form>
  );
}
